/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Archive, History as HistoryIcon, MoreHorizontal, Pencil, Plus, Sparkles } from 'lucide-react';
import { api, ApiError, type CompanySkillLimits, type CompanySkillRow, type PersonSkillRow, type PersonSkillsView } from '../lib/api';
import {
  AUTHOR_NAME_MAX, EMPTY_SKILL_FORM, aliasesOf, formProblems, isObjectId, personRefusalText, refusalField, skillRefusalText,
  type SkillForm, type SkillFormField,
} from '../lib/companySkills';
import { peopleIn } from '../lib/people';
import { PeoplePicker } from '../components/PeoplePicker';
import {
  Button, Callout, ConfirmDialog, Dialog, Drawer, EmptyState, ErrorState, Field, Input, MenuButton, PageHeader, Panel, Stack, StatusChip, Table, Tag, Textarea,
  type Column, type MenuItem,
} from '../ui';
import { plural } from '../ui/format';

function formOf(skill: CompanySkillRow): SkillForm {
  return { name: skill.name, description: skill.description, aliases: skill.aliases.join(', '), body: skill.body, authorName: skill.authorName ?? '' };
}

type PersonSkills =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; view: PersonSkillsView }
  | { status: 'failed'; text: string };

/** The drawer: a new skill, or an edit of one (a retired one comes back when saved). */
interface Editing {
  id: string;
  form: SkillForm;
  retired: boolean;
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Knowledge › Company skills (owner, 2026-10-12: "build personal skills and company skills"): the workflows an admin
 * publishes for everyone, which Gyro retrieves by name on every Work turn. Publish, edit (each edit is a new version),
 * retire (the skill stays with its history; nothing retrieves it), and promote a person's own skill as an independent
 * company copy carrying their name. Dark on a service with company skills off: the listing answers 404 and this screen
 * says so instead of showing an empty list. The drawer checks the service's limits before it sends, and every refusal is
 * said in plain words, by field where it names one.
 */
export function CompanySkills() {
  const [skills, setSkills] = useState<CompanySkillRow[]>([]);
  const [limits, setLimits] = useState<CompanySkillLimits | null>(null);
  const [off, setOff] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Publish or edit, in the drawer.
  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<SkillFormField, string>>>({});

  // Retire, confirmed.
  const [retiring, setRetiring] = useState<CompanySkillRow | null>(null);
  const [retireBusy, setRetireBusy] = useState(false);
  const [retireError, setRetireError] = useState<string | null>(null);

  // History, read only.
  const [history, setHistory] = useState<CompanySkillRow | null>(null);

  // Promote a person's skill: picked by address, or typed as an object id.
  const [person, setPerson] = useState('');
  const [objectId, setObjectId] = useState('');
  const [personSkills, setPersonSkills] = useState<PersonSkills>({ status: 'idle' });
  const [promoting, setPromoting] = useState<PersonSkillRow | null>(null);
  const [authorName, setAuthorName] = useState('');
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  const load = () => api.companySkills()
    .then((view) => { setSkills(view.skills); setLimits(view.limits); setOff(false); setLoadError(null); })
    .catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) setOff(true);
      else setLoadError(e);
    })
    .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  const personEmail = peopleIn(person)[0] ?? '';
  const typedObjectId = objectId.trim();
  const objectIdProblem = typedObjectId && !isObjectId(typedObjectId) ? 'An object id is 32 letters and digits in five groups, like 6f0c1a2b-3c4d-4e5f-8a9b-0c1d2e3f4a5b.' : undefined;
  // A whole object id wins over the picker: it is what the skills are kept under.
  const personKey = isObjectId(typedObjectId) ? typedObjectId.toLowerCase() : personEmail;
  useEffect(() => {
    if (!personKey) {
      setPersonSkills({ status: 'idle' });
      return;
    }
    let current = true;
    setPersonSkills({ status: 'loading' });
    api.personSkills(personKey).then(
      (view) => { if (current) setPersonSkills({ status: 'done', view }); },
      (e: unknown) => { if (current) setPersonSkills({ status: 'failed', text: personRefusalText(e) }); },
    );
    return () => { current = false; };
  }, [personKey]);

  const upsert = (saved: CompanySkillRow) => setSkills((rows) => (rows.some((row) => row.id === saved.id) ? rows.map((row) => (row.id === saved.id ? saved : row)) : [...rows, saved]));

  const openDrawer = (next: Editing) => {
    setFormError(null);
    setFieldErrors({});
    setEditing(next);
  };

  /** A change to one field clears that field's refusal. */
  const setField = (field: SkillFormField, value: string) => {
    if (!editing) return;
    setEditing({ ...editing, form: { ...editing.form, [field]: value } });
    if (fieldErrors[field]) setFieldErrors({ ...fieldErrors, [field]: undefined });
  };

  const save = async () => {
    if (!editing) return;
    const { id, form, retired } = editing;
    const problems = limits ? formProblems(form, limits) : {};
    if (Object.values(problems).some(Boolean)) {
      setFieldErrors(problems);
      setFormError('Nothing was sent. Fix the fields marked below.');
      return;
    }
    setSaving(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const author = form.authorName.trim();
      const saved = await api.publishCompanySkill(id, {
        name: form.name.trim(), description: form.description.trim(), aliases: aliasesOf(form.aliases), body: form.body,
        ...(id === 'new' ? (author ? { authorName: author } : {}) : { authorName: author || null }),
      });
      upsert(saved);
      setEditing(null);
      setNotice(id === 'new' ? `${saved.name} is published for everyone.` : retired ? `${saved.name} is back, as version ${saved.version}.` : `${saved.name} is now version ${saved.version}.`);
    } catch (e) {
      const text = skillRefusalText(e, 'The skill was not saved.');
      const field = refusalField(e);
      if (field) setFieldErrors({ [field]: text } as Partial<Record<SkillFormField, string>>);
      setFormError(field ? 'The service refused the field marked below. Nothing was saved.' : text);
    } finally {
      setSaving(false);
    }
  };

  const retire = async () => {
    if (!retiring) return;
    setRetireBusy(true);
    setRetireError(null);
    try {
      const retired = await api.retireCompanySkill(retiring.id);
      upsert(retired);
      setRetiring(null);
      setNotice(`${retired.name} is retired. Gyro no longer offers it; its history is kept.`);
    } catch (e) {
      setRetireError(skillRefusalText(e, 'The skill was not retired.'));
    } finally {
      setRetireBusy(false);
    }
  };

  const promote = async () => {
    if (!promoting || personSkills.status !== 'done') return;
    setPromoteBusy(true);
    setPromoteError(null);
    try {
      const promoted = await api.promoteSkill({ userId: personSkills.view.userId, skillId: promoting.id, authorName: authorName.trim() });
      upsert(promoted);
      setPromoting(null);
      setNotice(`${promoted.name} is published for everyone, by ${promoted.authorName ?? authorName.trim()}.`);
    } catch (e) {
      setPromoteError(skillRefusalText(e, 'The skill was not promoted.'));
    } finally {
      setPromoteBusy(false);
    }
  };

  const actionsFor = (skill: CompanySkillRow): MenuItem[] => [
    { id: 'edit', label: 'Edit', icon: Pencil, onSelect: () => openDrawer({ id: skill.id, form: formOf(skill), retired: skill.status === 'retired' }) },
    ...(skill.status === 'active' ? [{ id: 'retire', label: 'Retire', icon: Archive, onSelect: () => { setRetireError(null); setRetiring(skill); } }] : []),
    { id: 'history', label: 'History', icon: HistoryIcon, onSelect: () => setHistory(skill) },
  ];

  const columns: Array<Column<CompanySkillRow>> = [
    { key: 'name', header: 'Skill', cell: (s) => <span className="ui-cell-title">{s.name}{s.promotedFrom && <Tag mono={false}>promoted</Tag>}</span> },
    { key: 'description', header: 'Description', muted: true, cell: (s) => s.description || '—' },
    { key: 'version', header: 'Version', numeric: true, hideOnPhone: true, cell: (s) => s.version },
    { key: 'author', header: 'Author', muted: true, hideOnPhone: true, cell: (s) => s.authorName || '—' },
    { key: 'publishedBy', header: 'Published by', muted: true, hideOnPhone: true, cell: (s) => s.publishedBy },
    { key: 'status', header: 'Status', width: '110px', cell: (s) => (s.status === 'retired' ? <StatusChip tone="neutral">retired</StatusChip> : <StatusChip tone="ok">active</StatusChip>) },
    { key: 'actions', header: <span className="ui-sr">Actions</span>, width: '56px', align: 'end', cell: (s) => <MenuButton label={`Actions for ${s.name}`} icon={MoreHorizontal} items={actionsFor(s)} /> },
  ];

  const personColumns: Array<Column<PersonSkillRow>> = [
    { key: 'name', header: 'Skill', cell: (s) => s.name },
    { key: 'description', header: 'Description', muted: true, cell: (s) => s.description || '—' },
    { key: 'updated', header: 'Updated', muted: true, hideOnPhone: true, cell: (s) => when(s.updatedAt) },
    // The author name starts empty: Gyro holds no display name for a desktop sign-in, and an address is not a name.
    { key: 'promote', header: <span className="ui-sr">Actions</span>, align: 'end', cell: (s) => <Button size="sm" icon={Sparkles} onClick={() => { setPromoteError(null); setAuthorName(''); setPromoting(s); }}>Promote</Button> },
  ];

  const active = skills.filter((s) => s.status === 'active').length;
  const drawerDescription = editing?.id === 'new' ? 'Everyone can ask Gyro to run it by name' : editing?.retired ? 'It is retired. Saving publishes a new version and brings it back' : 'Saving publishes a new version';

  return (
    <>
      <PageHeader
        meta={<span>Skills published for everyone: Gyro runs them by name on every Work turn</span>}
        actions={<Button variant="primary" icon={Plus} disabled={off || loading} onClick={() => openDrawer({ id: 'new', form: EMPTY_SKILL_FORM, retired: false })}>Publish skill</Button>}
      />
      <div className="ui-page">
        <Stack>
          {notice && <Callout tone="ok" role="status">{notice}</Callout>}
          {off ? (
            <Callout tone="warn" title="Company skills are switched off" action={<Button size="sm" onClick={() => { setLoading(true); void load(); }}>Check again</Button>}>
              Nothing can be published until skills are switched on for Gyro. Whoever runs Gyro switches them on (the
              GYRO_COMPANY_SKILLS_ENABLED setting); then press Check again.
            </Callout>
          ) : loadError ? (
            <ErrorState title="The company skills could not be loaded" error={loadError} onRetry={() => { setLoading(true); void load(); }} retrying={loading} />
          ) : (
            <Panel
              flush
              title={<span className="ui-title-icon"><Sparkles size={16} strokeWidth={1.75} aria-hidden="true" />Published skills ({skills.length})</span>}
              description={loading ? 'Loading…' : `${plural(active, 'skill')} active${limits ? ` of ${limits.companySkills} the service keeps` : ''}`}
              footer={<span>An edit publishes a new version; the older ones stay in the skill's history. A retired skill is kept but never offered.</span>}
            >
              <Table stacked
                label="Published skills"
                columns={columns}
                rows={skills}
                rowKey={(s) => s.id}
                loading={loading}
                skeletonRows={4}
                empty={<EmptyState compact icon={Sparkles} title="No company skills yet">Publish one, or promote a skill someone saved.</EmptyState>}
              />
            </Panel>
          )}

          {!off && !loadError && (
            <Panel
              title="Promote a person's skill"
              description="Pick someone, then publish one of the skills they saved as a company skill carrying their name. The copy is independent: their later edits stay theirs."
            >
              <div className="ui-form">
                <div className="ui-field">
                  <label className="ui-field-label" htmlFor="promote-person">Person</label>
                  <PeoplePicker id="promote-person" label="Person" value={person} max={1} onChange={(value) => setPerson(peopleIn(value).slice(-1).join('\n'))} />
                  <p className="ui-field-note">Someone who has signed in to Gyro on their desktop. Their skills show below.</p>
                </div>
                <Field label="Or their Entra object id" help="For someone Gyro cannot find by address. It is used instead of the person above." error={objectIdProblem}>
                  <Input value={objectId} spellCheck={false} autoComplete="off" onChange={(e) => setObjectId(e.target.value)} placeholder="6f0c1a2b-3c4d-4e5f-8a9b-0c1d2e3f4a5b" />
                </Field>
                {personSkills.status === 'failed' && <Callout tone="bad" role="alert">{personSkills.text}</Callout>}
                {personSkills.status === 'done' && (
                  <Table stacked
                    label={`Skills saved by ${personKey}`}
                    columns={personColumns}
                    rows={personSkills.view.skills}
                    rowKey={(s) => s.id}
                    empty={<EmptyState compact title="No saved skills">{personKey} has not saved a skill yet.</EmptyState>}
                  />
                )}
                {personSkills.status === 'loading' && <span className="ui-cell-sub" role="status">Reading their skills…</span>}
              </div>
            </Panel>
          )}
        </Stack>
      </div>

      <Drawer
        open={editing !== null}
        onClose={() => { if (!saving) setEditing(null); }}
        title={editing?.id === 'new' ? 'Publish a skill' : `Edit ${editing?.form.name || 'skill'}`}
        description={drawerDescription}
        footer={(
          <>
            <Button variant="ghost" disabled={saving} onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" busy={saving} onClick={() => void save()}>{editing?.id === 'new' ? 'Publish' : editing?.retired ? 'Save and bring back' : 'Save'}</Button>
          </>
        )}
      >
        {editing && (
          <div className="ui-form">
            {formError && <Callout tone="bad" role="alert">{formError}</Callout>}
            <Field label="Name" help="What people say to run it: letters, digits, spaces, dots, underscores or hyphens" error={fieldErrors.name}>
              <Input value={editing.form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Weekly status" />
            </Field>
            <Field label="Description" help="One line on what it does" error={fieldErrors.description}>
              <Input value={editing.form.description} onChange={(e) => setField('description', e.target.value)} />
            </Field>
            <Field label="Aliases" help={`Other phrases for it, separated by commas${limits ? ` (at most ${limits.aliases})` : ''}`} error={fieldErrors.aliases}>
              <Input value={editing.form.aliases} onChange={(e) => setField('aliases', e.target.value)} placeholder="status report, weekly update" />
            </Field>
            <Field label="Steps" help={`The workflow, as plain text${limits ? ` (at most ${limits.body.toLocaleString('en-US')} characters)` : ''}`} error={fieldErrors.body}>
              <Textarea rows={12} value={editing.form.body} onChange={(e) => setField('body', e.target.value)} />
            </Field>
            <Field label="Written by" help="Shown beside the skill; leave empty for none" error={fieldErrors.authorName}>
              <Input value={editing.form.authorName} onChange={(e) => setField('authorName', e.target.value)} />
            </Field>
          </div>
        )}
      </Drawer>

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire ${retiring?.name ?? 'this skill'}?`}
        confirmLabel="Retire"
        busy={retireBusy}
        error={retireError}
        onConfirm={() => void retire()}
        onCancel={() => setRetiring(null)}
      >
        Gyro stops offering it to everyone. The skill and its history are kept, and an edit brings it back.
      </ConfirmDialog>

      <Dialog
        open={history !== null}
        onClose={() => setHistory(null)}
        title={`History of ${history?.name ?? ''}`}
        description={history ? `Version ${history.version}, published by ${history.publishedBy}` : undefined}
      >
        {history && (
          <Table
            label={`Versions of ${history.name}`}
            columns={[
              { key: 'version', header: 'Version', numeric: true, cell: (r) => r.version },
              { key: 'name', header: 'Name', cell: (r) => r.name },
              { key: 'at', header: 'Published', muted: true, cell: (r) => when(r.at) },
              { key: 'by', header: 'By', muted: true, cell: (r) => r.by },
            ] as Array<Column<CompanySkillRow['history'][number]>>}
            rows={history.history}
            rowKey={(r) => String(r.version)}
            empty={<EmptyState compact title="No versions recorded" />}
          />
        )}
      </Dialog>

      <Dialog
        open={promoting !== null}
        onClose={() => { if (!promoteBusy) setPromoting(null); }}
        title={`Promote ${promoting?.name ?? ''}`}
        description="Published for everyone as a company skill, with the author's name beside it"
        footer={(
          <>
            <Button variant="ghost" disabled={promoteBusy} onClick={() => setPromoting(null)}>Cancel</Button>
            <Button variant="primary" busy={promoteBusy} disabled={!authorName.trim()} onClick={() => void promote()}>Promote</Button>
          </>
        )}
      >
        <div className="ui-form">
          {promoteError && <Callout tone="bad" role="alert">{promoteError}</Callout>}
          <Field label="Author name" help="How the person is named beside the skill, such as Pat Lee">
            <Input value={authorName} maxLength={AUTHOR_NAME_MAX} onChange={(e) => setAuthorName(e.target.value)} />
          </Field>
          {promoting?.body && (
            <Field label="Steps" help="As the person saved them; edit the company copy afterwards if needed">
              <Textarea rows={8} readOnly value={promoting.body} />
            </Field>
          )}
        </div>
      </Dialog>
    </>
  );
}
